import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
    Text,
    Button,
    Card,
    makeStyles,
    tokens
} from '@fluentui/react-components';
import {
    ErrorCircle24Regular,
    ArrowClockwise20Regular,
    Warning20Regular
} from '@fluentui/react-icons';
import { createErrorContext, type ErrorContext } from '@/utils/agentMessageValidator';
import { ErrorLogger } from '@/utils/ErrorLogger';

/**
 * Props for the AgentMessageErrorBoundary component
 */
interface AgentMessageErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo, context: ErrorContext) => void;
    showRetry?: boolean;
    componentName?: string;
}

/**
 * State for the AgentMessageErrorBoundary component
 */
interface AgentMessageErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
    errorId: string;
    retryCount: number;
}

/**
 * Styles for the error boundary components
 */
const useStyles = makeStyles({
    errorContainer: {
        padding: '16px',
        margin: '8px 0',
        borderRadius: '8px',
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorPaletteRedBorder2}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '100%'
    },
    errorHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: tokens.colorPaletteRedForeground2
    },
    errorIcon: {
        fontSize: '20px',
        color: tokens.colorPaletteRedForeground2
    },
    errorTitle: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorPaletteRedForeground2
    },
    errorMessage: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        wordBreak: 'break-word'
    },
    errorActions: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        marginTop: '8px'
    },
    retryButton: {
        minWidth: '80px'
    },
    errorDetails: {
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
        fontFamily: tokens.fontFamilyMonospace,
        backgroundColor: tokens.colorNeutralBackground1,
        padding: '8px',
        borderRadius: '4px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        maxHeight: '120px',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all'
    },
    warningIcon: {
        color: tokens.colorPaletteYellowForeground2,
        fontSize: '16px'
    }
});

/**
 * Error placeholder component for displaying error information
 */
interface ErrorPlaceholderProps {
    error?: Error;
    errorId: string;
    onRetry?: () => void;
    showRetry?: boolean;
    retryCount: number;
    componentName?: string;
}

const ErrorPlaceholder: React.FC<ErrorPlaceholderProps> = ({
    error,
    errorId,
    onRetry,
    showRetry = true,
    retryCount,
    componentName = 'Agent Message'
}) => {
    const styles = useStyles();
    const [showDetails, setShowDetails] = React.useState(false);

    const errorMessage = error?.message || 'An unexpected error occurred while rendering the agent message.';
    const isRetryLimitReached = retryCount >= 3;

    return (
        <Card className={styles.errorContainer}>
            <div className={styles.errorHeader}>
                <ErrorCircle24Regular className={styles.errorIcon} />
                <Text className={styles.errorTitle}>
                    {componentName} Error
                </Text>
            </div>

            <Text className={styles.errorMessage}>
                {errorMessage}
            </Text>

            {retryCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Warning20Regular className={styles.warningIcon} />
                    <Text size={200} style={{ color: tokens.colorPaletteYellowForeground2 }}>
                        Retry attempt {retryCount} of 3
                    </Text>
                </div>
            )}

            <div className={styles.errorActions}>
                {showRetry && !isRetryLimitReached && onRetry && (
                    <Button
                        appearance="primary"
                        size="small"
                        icon={<ArrowClockwise20Regular />}
                        onClick={onRetry}
                        className={styles.retryButton}
                    >
                        Retry
                    </Button>
                )}

                <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setShowDetails(!showDetails)}
                >
                    {showDetails ? 'Hide Details' : 'Show Details'}
                </Button>
            </div>

            {showDetails && error && (
                <div className={styles.errorDetails}>
                    <Text size={100}>Error ID: {errorId}</Text>
                    {error.stack && (
                        <>
                            <br />
                            <Text size={100}>Stack Trace:</Text>
                            <br />
                            {error.stack}
                        </>
                    )}
                </div>
            )}

            {isRetryLimitReached && (
                <Text size={200} style={{ color: tokens.colorPaletteRedForeground2 }}>
                    Maximum retry attempts reached. Please refresh the page or contact support if the issue persists.
                </Text>
            )}
        </Card>
    );
};

/**
 * Error logger utility for agent message error boundaries
 */
class AgentMessageErrorLogger {
    /**
     * Log error with comprehensive context information using the main ErrorLogger
     */
    static logError(
        error: Error,
        errorInfo: ErrorInfo,
        context: ErrorContext,
        componentName: string = 'AgentMessageErrorBoundary'
    ): void {
        // Use the main ErrorLogger for consistent logging
        const errorLogger = ErrorLogger.getInstance();
        
        // Log the error with additional React error boundary context
        errorLogger.logError(
            componentName,
            error,
            {
                componentStack: errorInfo.componentStack,
                errorBoundary: true,
                context
            },
            'componentDidCatch'
        );
    }

    /**
     * Log retry attempt using the main ErrorLogger
     */
    static logRetry(
        componentName: string,
        retryCount: number,
        errorId: string
    ): void {
        const errorLogger = ErrorLogger.getInstance();
        errorLogger.logInfo(
            componentName,
            `Retry attempt ${retryCount} for error ${errorId}`,
            { retryCount, errorId },
            'retry'
        );
    }

    /**
     * Log successful recovery using the main ErrorLogger
     */
    static logRecovery(
        componentName: string,
        retryCount: number,
        errorId: string
    ): void {
        const errorLogger = ErrorLogger.getInstance();
        errorLogger.logInfo(
            componentName,
            `Successfully recovered after ${retryCount} retries for error ${errorId}`,
            { retryCount, errorId, recovered: true },
            'recovery'
        );
    }
}

/**
 * React Error Boundary for Agent Message Components
 * 
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * 
 * Features:
 * - Comprehensive error logging with context information
 * - Retry functionality with attempt limits
 * - Customizable fallback UI
 * - Error isolation to prevent complete UI failure
 * - Structured error reporting for debugging
 */
export class AgentMessageErrorBoundary extends Component<
    AgentMessageErrorBoundaryProps,
    AgentMessageErrorBoundaryState
> {
    private retryTimeoutId: NodeJS.Timeout | null = null;

    constructor(props: AgentMessageErrorBoundaryProps) {
        super(props);
        
        this.state = {
            hasError: false,
            errorId: '',
            retryCount: 0
        };
    }

    /**
     * Static method called when an error is thrown during rendering
     */
    static getDerivedStateFromError(error: Error): Partial<AgentMessageErrorBoundaryState> {
        // Generate unique error ID for tracking
        const errorId = `agent-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return {
            hasError: true,
            error,
            errorId
        };
    }

    /**
     * Called when an error is caught by the error boundary
     */
    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        const { onError, componentName = 'AgentMessage' } = this.props;
        
        // Create error context with comprehensive information
        const errorContext = createErrorContext(
            'AgentMessageErrorBoundary',
            'componentDidCatch',
            {
                agentName: this.extractAgentNameFromProps(),
                agentType: this.extractAgentTypeFromProps(),
                originalData: this.extractOriginalDataFromProps()
            }
        );

        // Log error with full context
        AgentMessageErrorLogger.logError(error, errorInfo, errorContext, componentName);

        // Call custom error handler if provided
        if (onError) {
            try {
                onError(error, errorInfo, errorContext);
            } catch (handlerError) {
                console.error('Error in custom error handler:', handlerError);
            }
        }

        // Update state with error information
        this.setState({
            error,
            errorInfo
        });
    }

    /**
     * Extract agent name from props for context
     */
    private extractAgentNameFromProps(): string | undefined {
        try {
            const { children } = this.props;
            if (React.isValidElement(children) && children.props) {
                return children.props.agentName || 
                       children.props.agent || 
                       (children.props.agentMessages?.[0]?.agent);
            }
        } catch (error) {
            // Ignore extraction errors
        }
        return undefined;
    }

    /**
     * Extract agent type from props for context
     */
    private extractAgentTypeFromProps(): string | undefined {
        try {
            const { children } = this.props;
            if (React.isValidElement(children) && children.props) {
                return children.props.agentType || 
                       children.props.agent_type || 
                       (children.props.agentMessages?.[0]?.agent_type);
            }
        } catch (error) {
            // Ignore extraction errors
        }
        return undefined;
    }

    /**
     * Extract original data from props for context
     */
    private extractOriginalDataFromProps(): any {
        try {
            const { children } = this.props;
            if (React.isValidElement(children) && children.props) {
                return {
                    agentMessages: children.props.agentMessages,
                    planData: children.props.planData,
                    planApprovalRequest: children.props.planApprovalRequest
                };
            }
        } catch (error) {
            // Ignore extraction errors
        }
        return undefined;
    }

    /**
     * Handle retry attempt
     */
    private handleRetry = (): void => {
        const { componentName = 'AgentMessage' } = this.props;
        const { errorId, retryCount } = this.state;
        
        const newRetryCount = retryCount + 1;
        
        // Log retry attempt
        AgentMessageErrorLogger.logRetry(componentName, newRetryCount, errorId);
        
        // Clear any existing timeout
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }
        
        // Reset error state with incremented retry count
        this.setState({
            hasError: false,
            error: undefined,
            errorInfo: undefined,
            retryCount: newRetryCount
        });
        
        // Set timeout to log recovery if successful
        this.retryTimeoutId = setTimeout(() => {
            if (!this.state.hasError) {
                AgentMessageErrorLogger.logRecovery(componentName, newRetryCount, errorId);
            }
        }, 1000);
    };

    /**
     * Cleanup on unmount
     */
    componentWillUnmount(): void {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }
    }

    /**
     * Render method
     */
    render(): ReactNode {
        const { children, fallback, showRetry = true, componentName } = this.props;
        const { hasError, error, errorId, retryCount } = this.state;

        if (hasError) {
            // Use custom fallback if provided
            if (fallback) {
                return fallback;
            }

            // Use default error placeholder
            return (
                <ErrorPlaceholder
                    error={error}
                    errorId={errorId}
                    onRetry={showRetry ? this.handleRetry : undefined}
                    showRetry={showRetry}
                    retryCount={retryCount}
                    componentName={componentName}
                />
            );
        }

        return children;
    }
}

export default AgentMessageErrorBoundary;